import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

const base = `${API}/api/sqf`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// ─── Templates ────────────────────────────────────────────────────────────────
const getTemplates = () => axios.get(`${base}/templates`, auth()).then(r => r.data.templates);
const getTemplate = (id) => axios.get(`${base}/templates/${id}`, auth()).then(r => r.data.template);
const createTemplate = (data) => axios.post(`${base}/templates`, data, auth()).then(r => r.data.template);
const updateTemplate = (id, data) => axios.put(`${base}/templates/${id}`, data, auth()).then(r => r.data.template);
const archiveTemplate = (id) => axios.delete(`${base}/templates/${id}`, auth()).then(r => r.data);

// ─── Template Versions ────────────────────────────────────────────────────────
const getVersions = (templateId) => axios.get(`${base}/templates/${templateId}/versions`, auth()).then(r => r.data.versions);
const createVersion = (templateId, data) => axios.post(`${base}/templates/${templateId}/versions`, data, auth()).then(r => r.data.version);
const updateVersion = (templateId, vId, data) => axios.put(`${base}/templates/${templateId}/versions/${vId}`, data, auth()).then(r => r.data.version);
const publishVersion = (templateId, vId, data = {}) => axios.post(`${base}/templates/${templateId}/versions/${vId}/publish`, data, auth()).then(r => r.data);
const deleteVersion = (templateId, vId) => axios.delete(`${base}/templates/${templateId}/versions/${vId}`, auth()).then(r => r.data);

// ─── Schedules ────────────────────────────────────────────────────────────────
const getSchedules = () => axios.get(`${base}/schedules`, auth()).then(r => r.data.schedules);
const getScheduleOverview = () => axios.get(`${base}/schedules/overview`, auth()).then(r => r.data.overview);
const createSchedule = (data) => axios.post(`${base}/schedules`, data, auth()).then(r => r.data.schedule);
const updateSchedule = (id, data) => axios.put(`${base}/schedules/${id}`, data, auth()).then(r => r.data.schedule);
const deleteSchedule = (id) => axios.delete(`${base}/schedules/${id}`, auth()).then(r => r.data);

// ─── Instances ────────────────────────────────────────────────────────────────
const getInstances = (params = {}) => axios.get(`${base}/instances`, { ...auth(), params }).then(r => r.data.instances);
const getTodayInstances = () => axios.get(`${base}/instances/today`, auth()).then(r => r.data.instances);
const getInstance = (id) => axios.get(`${base}/instances/${id}`, auth()).then(r => r.data.instance);
// Full response ({ instance, previousItemLists }) — needed by the Fill page to pre-populate an
// item_count_list field from the most recent prior submission; getInstance() above only ever
// returns the instance itself.
const getInstanceFull = (id) => axios.get(`${base}/instances/${id}`, auth()).then(r => r.data);
const createInstance = (data) => axios.post(`${base}/instances`, data, auth()).then(r => r.data.instance);
const assignInstance = (id, assignedUserId) => axios.patch(`${base}/instances/${id}/assign`, { assignedUserId }, auth()).then(r => r.data.instance);

// ─── Logs ─────────────────────────────────────────────────────────────────────
const startLog = (instanceId) => axios.post(`${base}/instances/${instanceId}/start`, {}, auth()).then(r => r.data.log);
const saveResponses = (logId, responses) => axios.put(`${base}/logs/${logId}/responses`, { responses }, auth()).then(r => r.data);
const submitLog = (logId, data) => axios.post(`${base}/logs/${logId}/submit`, data, auth()).then(r => r.data);
const getLog = (logId) => axios.get(`${base}/logs/${logId}`, auth()).then(r => r.data.log);
const getPendingQaLogs = () => axios.get(`${base}/logs/pending-qa`, auth()).then(r => r.data.logs);
const approveLog = (logId, qaNotes) => axios.post(`${base}/logs/${logId}/qa-approve`, { qaNotes }, auth()).then(r => r.data.log);
const rejectLog = (logId, qaNotes) => axios.post(`${base}/logs/${logId}/qa-reject`, { qaNotes }, auth()).then(r => r.data.log);

// ─── Corrective Actions ───────────────────────────────────────────────────────
const getCorrectiveActions = (params = {}) => axios.get(`${base}/corrective-actions`, { ...auth(), params }).then(r => r.data.actions);
const createCorrectiveAction = (data) => axios.post(`${base}/corrective-actions`, data, auth()).then(r => r.data.action);
const updateCorrectiveAction = (id, data) => axios.put(`${base}/corrective-actions/${id}`, data, auth()).then(r => r.data.action);
const resolveCorrectiveAction = (id) => axios.post(`${base}/corrective-actions/${id}/resolve`, {}, auth()).then(r => r.data.action);
const verifyCorrectiveAction = (id) => axios.post(`${base}/corrective-actions/${id}/verify`, {}, auth()).then(r => r.data.action);

// ─── Contractors ──────────────────────────────────────────────────────────────
const getContractors = (params = {}) => axios.get(`${base}/contractors`, { ...auth(), params }).then(r => r.data.contractors);
const createContractor = (data) => axios.post(`${base}/contractors`, data, auth()).then(r => r.data.contractor);
const updateContractor = (id, data) => axios.put(`${base}/contractors/${id}`, data, auth()).then(r => r.data.contractor);
const updateContractorAgreement = (id, data) => axios.post(`${base}/contractors/${id}/agreement`, data, auth()).then(r => r.data.contractor);

// ─── Policies ─────────────────────────────────────────────────────────────────
const getPolicies = () => axios.get(`${base}/policies`, auth()).then(r => r.data.policies);
const createPolicy = (data) => axios.post(`${base}/policies`, data, auth()).then(r => r.data.policy);
const getPolicy = (id) => axios.get(`${base}/policies/${id}`, auth()).then(r => r.data.policy);
const updatePolicy = (id, data) => axios.put(`${base}/policies/${id}`, data, auth()).then(r => r.data.policy);
const archivePolicy = (id) => axios.delete(`${base}/policies/${id}`, auth()).then(r => r.data);
const deletePolicyPermanent = (id) => axios.delete(`${base}/policies/${id}/permanent`, auth()).then(r => r.data);
const createPolicyVersion = (policyId, data) => axios.post(`${base}/policies/${policyId}/versions`, data, auth()).then(r => r.data.version);
const updatePolicyVersion = (policyId, vId, data) => axios.put(`${base}/policies/${policyId}/versions/${vId}`, data, auth()).then(r => r.data.version);
const publishPolicyVersion = (policyId, vId) => axios.post(`${base}/policies/${policyId}/versions/${vId}/publish`, {}, auth()).then(r => r.data);
const deletePolicyVersion  = (policyId, vId) => axios.delete(`${base}/policies/${policyId}/versions/${vId}`, auth()).then(r => r.data);

// ─── Visitor Logs ─────────────────────────────────────────────────────────────
const getVisitorLogs = (params = {}) => axios.get(`${base}/visitor-logs`, { ...auth(), params }).then(r => r.data);
const createVisitorLog = (data) => axios.post(`${base}/visitor-logs`, data, auth()).then(r => r.data.log);
const updateVisitorLog = (id, data) => axios.put(`${base}/visitor-logs/${id}`, data, auth()).then(r => r.data.log);
const signVisitorLog = (id, data) => axios.post(`${base}/visitor-logs/${id}/sign`, data, auth()).then(r => r.data.log);

// ─── Notifications ────────────────────────────────────────────────────────────
const getNotifications = () => axios.get(`${base}/notifications`, auth()).then(r => r.data.notifications);
const markNotificationRead = (id) => axios.post(`${base}/notifications/${id}/read`, {}, auth()).then(r => r.data.notification);

// ─── Production Gate ──────────────────────────────────────────────────────────
const getProductionGate = () => axios.get(`${base}/gate/before_production`, auth()).then(r => r.data);
const prepareProductionGate = () => axios.post(`${base}/gate/before_production/prepare`, {}, auth()).then(r => r.data);
const getAfterProductionGate = () => axios.get(`${base}/gate/after_production`, auth()).then(r => r.data);
const prepareAfterProductionGate = () => axios.post(`${base}/gate/after_production/prepare`, {}, auth()).then(r => r.data);

// ─── Instance lock ────────────────────────────────────────────────────────────
const lockInstance = (id) => axios.post(`${base}/instances/${id}/lock`, {}, auth()).then(r => r.data);
const releaseLock = (id) => axios.delete(`${base}/instances/${id}/lock`, auth()).then(r => r.data);

// ─── Audit Hub ────────────────────────────────────────────────────────────────
const getHub          = ()     => axios.get(`${base}/hub`, auth()).then(r => r.data);
const getClauses      = ()     => axios.get(`${base}/clauses`, auth()).then(r => r.data.clauses);
const createClause    = (data) => axios.post(`${base}/clauses`, data, auth()).then(r => r.data.clause);
const deleteClause    = (id)   => axios.delete(`${base}/clauses/${id}`, auth()).then(r => r.data);
const deleteAllClauses = ()    => axios.delete(`${base}/clauses/all`, auth()).then(r => r.data);
const saveCoverageLink = (data) => axios.post(`${base}/coverage`, data, auth()).then(r => r.data.link);
const deleteCoverageLink = (id) => axios.delete(`${base}/coverage/${id}`, auth()).then(r => r.data);
const addClauseAttachment = (clauseId, data) => axios.post(`${base}/clauses/${clauseId}/attachments`, data, auth()).then(r => r.data.attachment);
const deleteClauseAttachment = (id) => axios.delete(`${base}/attachments/${id}`, auth()).then(r => r.data);

// ─── AI Usage ─────────────────────────────────────────────────────────────────
const getAiUsage = () => axios.get(`${base}/ai-usage`, auth()).then(r => r.data);

// ─── Dashboard ────────────────────────────────────────────────────────────────
const getDashboard = () => axios.get(`${base}/dashboard`, auth()).then(r => r.data);

// ─── Facility Profile ─────────────────────────────────────────────────────────
const updateFacilityProfile = (profile) => axios.patch(`${base}/facility-profile`, { sqfFacilityProfile: profile }, auth()).then(r => r.data);

// ─── Applicability ────────────────────────────────────────────────────────────
const updateClauseApplicability = (id, data) => axios.patch(`${base}/clauses/${id}/applicability`, data, auth()).then(r => r.data);

// ─── Clause Chat ──────────────────────────────────────────────────────────────
const clauseChat = (question) => axios.post(`${base}/ai/clause-chat`, { question }, auth()).then(r => r.data);

// ─── Visual Inspection (AI-assisted) ───────────────────────────────────────────
const getVisualInspectionUploadUrl = (contentType) =>
  axios.post(`${base}/visual-inspections/upload-url`, { contentType }, auth()).then(r => r.data);
const analyzeVisualInspection = (photoKey, productId, criteria) =>
  axios.post(`${base}/visual-inspections/analyze`, { photoKey, productId, criteria }, auth()).then(r => r.data);
const getChecklistLogsForBatch = (batchId, productId) =>
  axios.get(`${base}/checklist-logs`, { ...auth(), params: { batchId, productId } }).then(r => r.data.logs);
const getVisualInspectionPhotoUrl = (key) =>
  axios.get(`${base}/visual-inspections/photo-url`, { ...auth(), params: { key } }).then(r => r.data.url);
const getAvailableBatchesForInspection = (days) =>
  axios.get(`${base}/visual-inspections/available-batches`, { ...auth(), params: { days } }).then(r => r.data.batches);

const sqfService = {
  // Templates
  getTemplates, getTemplate, createTemplate, updateTemplate, archiveTemplate,
  // Versions
  getVersions, createVersion, updateVersion, publishVersion, deleteVersion,
  // Schedules
  getSchedules, getScheduleOverview, createSchedule, updateSchedule, deleteSchedule,
  // Instances
  getInstances, getTodayInstances, getInstance, getInstanceFull, createInstance, assignInstance,
  // Logs
  startLog, saveResponses, submitLog, getLog, getPendingQaLogs, approveLog, rejectLog,
  // Corrective Actions
  getCorrectiveActions, createCorrectiveAction, updateCorrectiveAction,
  resolveCorrectiveAction, verifyCorrectiveAction,
  // Contractors
  getContractors, createContractor, updateContractor, updateContractorAgreement,
  // Visitor Logs
  getVisitorLogs, createVisitorLog, updateVisitorLog, signVisitorLog,
  // Policies
  getPolicies, createPolicy, getPolicy, updatePolicy, archivePolicy, deletePolicyPermanent,
  createPolicyVersion, updatePolicyVersion, publishPolicyVersion, deletePolicyVersion,
  // Notifications
  getNotifications, markNotificationRead,
  // Gate
  getProductionGate, prepareProductionGate, getAfterProductionGate, prepareAfterProductionGate,
  // Lock
  lockInstance, releaseLock,
  // AI Usage
  getAiUsage,
  // Dashboard
  getDashboard,
  // Audit Hub
  getHub, getClauses, createClause, deleteClause, deleteAllClauses, saveCoverageLink, deleteCoverageLink,
  addClauseAttachment, deleteClauseAttachment,
  // Facility Profile & Applicability
  updateFacilityProfile, updateClauseApplicability,
  // Clause Chat
  clauseChat,
  // Visual Inspection
  getVisualInspectionUploadUrl, analyzeVisualInspection, getChecklistLogsForBatch, getVisualInspectionPhotoUrl,
  getAvailableBatchesForInspection,
};

export default sqfService;
